import { useTranslation } from 'next-i18next'
import React, { ReactNode, useEffect } from 'react'
import { useFlag } from '../../hooks'
import { Head, MonthlyPaymentsPanel, StoresPanel, UpcomingPaymentsPanel, WelcomePanel, MonthlyPaymentsCreditFacilityPanel } from '../../component'
import { ConsumerPage } from '../../model'
import { useConsumerHasCreditFacility, useConsumerSelector, useAppDispatch, clearToast } from '../../state'
import { TrackPageViewed, useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import styles from './HomePage.module.scss'
import { useToast, ToastItem } from '@afterpaytouch/core'
import { TrackingEvent } from '../../model/amplitude'

const I18N_NAMESPACE = [
  'common',
  'terminalError',
  'index',
  'account',
  'payments',
  'upcomingPayments',
  'cardScan',
  'oauth',
  'ach',
  'orders',
  'loyalty',
  'payMonthly',
]

export const HomePage: ConsumerPage = () => {
  const isConsumerPortalMonthlyPaymentsLendingEnabled = useFlag('consumer-portal-monthly-payments-lending-enabled')
  const isConsumerPortalStorePanelEnabled = useFlag('consumer-portal-store-panel-enabled')
  const hasCreditFacility = useConsumerHasCreditFacility()
  const setToast = useToast()
  const dispatch = useAppDispatch()
  const consumer = useConsumerSelector()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { toast } = consumer

  const {
    t,
    i18n: { language: locale },
  } = useTranslation(I18N_NAMESPACE)

  const pageTitle = `${t('index:title')} | ${t('common:brandName')}`

  // Check for toast state and show toast
  useEffect(() => {
    const toastType = toast?.toastType
    const toastTime = toast?.toastTime ?? 10000
    const showToastBody = toast?.showToastBody

    if (toastType != null && toastType.length > 0) {
      const toastComponent = (): JSX.Element => (
        <>
          <ToastItem.Headline>{t(`common:${toastType}Header`)}</ToastItem.Headline>
          {Boolean(showToastBody) && <ToastItem.Body>{t(`common:${toastType}Message`)}</ToastItem.Body>}
        </>
      )

      setToast({
        message: toastComponent,
        // @ts-expect-error Toast kind type mismatch in ToastItem definition
        kind: toast?.toastKind,
        toastTime,
        testNameSpace: toastType,
      })
      logEvent(TrackingEvent.VIEW_TOAST_ON_HOME_PAGE, {
        toastType,
      })
      dispatch(clearToast())
    }
  }, [toast])

  const renderMonthlyPaymentsPanel = (): ReactNode => {
    if (!isConsumerPortalMonthlyPaymentsLendingEnabled) {
      return
    }

    if (locale === 'en-AU' && !hasCreditFacility) {
      return <MonthlyPaymentsCreditFacilityPanel />
    }
    if (locale === 'en-US') {
      return <MonthlyPaymentsPanel />
    }
  }
  return (
    <div className={styles.container}>
      <Head title={pageTitle} />
      <WelcomePanel />
      <UpcomingPaymentsPanel />
      {renderMonthlyPaymentsPanel()}
      {isConsumerPortalStorePanelEnabled && <StoresPanel title='index:stores:title:new' category='index:stores:category:new' />}
      {isConsumerPortalStorePanelEnabled && <StoresPanel title='index:stores:title:trendingNow' category='index:stores:category:trending' />}
      {isConsumerPortalStorePanelEnabled && <StoresPanel title='index:stores:title:shopSmall' category='index:stores:category:shopSmall' />}
      <TrackPageViewed />
    </div>
  )
}
